import { useDispatch, useSelector } from 'react-redux';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { addManyAutomationsToStore, loadAutomationsToStore } from 'store/actions/automation';
import WebDatatable from 'app/shared/datatable/web/datatable';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';
import { setPageTitle } from 'store/actions/header';
import { Button } from 'app/shared/button';
import ConfirmationDialog from 'app/shared/dialogs/confirmation';
import { toast } from 'react-toastify';
import { useAutomationTemplateService } from 'hooks/automation/template';
import { truncateText } from 'app/shared/utils/general';
import { AutomationStatBar } from './stats';
import { Spacer } from 'app/layouts/generic';
import { PermissionsContext } from 'contexts/permissions';
import { useEventsService } from 'hooks/automation/event';
import { filter_config } from './config/filter';
import { PageContentWrapper } from 'app/layouts/wrapper/page-content';
import { useSubscriptionService } from 'hooks/sales/subscription';
import useScreenSize from 'hooks/size';
import { Badge } from 'app/shared/badge';
import { SegmentedTabs } from 'app/shared/segmented-tabs';

// Status is a tab rather than a filter; each value is the status= query it sends.
const status_tabs = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];

export const WorkflowModule = () => {
  const history = useNavigate();
  const dispatch = useDispatch();
  const { isMobile } = useScreenSize();
  const automations_in_store = useSelector((state) => state.automation);

  const { fetchEvents } = useEventsService();
  const { fetchTemplates, searchTemplates, updateTemplates } = useAutomationTemplateService();
  const { fetchSubscription } = useSubscriptionService();

  const [automations, setAutomations] = useState([]);
  const [events, setEvents] = useState([]);
  const [has_subscription, setHasSubscription] = useState(false);
  const [is_search_mode, setSearchMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [total_automations, setTotalAutomations] = useState(0);
  const [status_tab, setStatusTab] = useState('active');
  const { permissions } = useContext(PermissionsContext);

  //
  const [action_to_take, setActionToTake] = useState('');
  const [items_to_act_on, setItemsToActOn] = useState([]);
  const [show_confirmation_dialog, setShowConfirmationDialog] = useState(false);

  useEffect(() => {
    dispatch(setPageTitle([{ title: 'Journeys', path: '.' }]));

    fetchEvents({ query_string: `type=user` }).then(({ events }) => {
      setEvents(events.map((event) => ({ label: event.name, value: event.code })));
    });

    fetchSubscription({ query_string: 'plan_code=campaign&status=active' }).then(
      ({ subscriptions: [subscription] }) => {
        setHasSubscription(() => subscription && subscription.tier === 'enterprise');
      }
    );
  }, []);

  useEffect(() => {
    setAutomations(Object.values(automations_in_store));
  }, [automations_in_store]);

  const config = {
    actions: {
      single: (automation) => [
        [
          { label: 'View/Edit', value: 'view', hidden: !permissions['automation:update'] },
          {
            label: 'Activate',
            value: 'activate',
            hidden: automation.status === 'active' || !permissions['automation:update']
          }
        ],
        [
          {
            color: 'red',
            label: 'Deactivate',
            value: 'deactivate',
            hidden: automation.status !== 'active' || !permissions['automation:update']
          }
        ]
      ]
    },
    allow_bulk_action: false,
    css: {},
    fields: [
      {
        title: 'Title',
        key: 'title',
        isTitle: true,
        sortable: true,
        formatter: (value) => truncateText(value, 35)
      },
      {
        title: 'Description',
        key: 'description',
        isTagline: true
      },
      {
        title: 'Status',
        key: 'status',
        sortable: true,
        formatter: (value) => {
          switch (value) {
            case 'inactive':
              return <Badge type="red" text="inactive" />;
            case 'active':
              return <Badge type="green" text="active" />;
          }
        },
        isBadge: true
      },
      {
        title: 'Date Created',
        key: 'created_on',
        sortable: true,
        formatter: (value) => {
          return value ? new Date(value).toDateString() : '';
        },
        isMetadata: true
      }
    ],
    is_search_mode,
    items: automations,
    num_of_rows: 10,
    search_key: 'title',
    search_text: ''
  };

  const handleDataRequest = async (
    page,
    population = 10,
    options = { filter_query: '', sort_query: '' }
  ) => {
    try {
      setLoading(true);
      const query = `status=${status_tab}&${options.filter_query || ''}`;
      const sort_query = options.sort_query || 'sort_by=-created_on';
      const { templates, size, error } = await fetchTemplates({
        query_string: `${query}&page=${page}&population=${population}&${sort_query}`
      });
      if (error) return toast.error(error);

      setTotalAutomations(size);
      dispatch(loadAutomationsToStore(templates));
    } catch (e) {
      dispatch(loadAutomationsToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleDatatableAction = (payload) => {
    const { name, type, data } = payload;
    if (type === 'single') {
      switch (name) {
        case 'view':
          history(`/editor/automations/${data.id}`);
          break;
        case 'deactivate':
          setActionToTake(name);
          setItemsToActOn([data]);
          setShowConfirmationDialog(() => true);
          break;
        case 'activate':
          setActionToTake(name);
          setItemsToActOn([data]);
          setShowConfirmationDialog(() => true);
          break;
        default:
      }
    }
  };

  const handleStatusConfirmation = async (is_permitted) => {
    if (is_permitted) {
      const status = action_to_take === 'activate' ? 'active' : 'inactive';
      const ids = items_to_act_on.map((template) => template.id).toString();
      const result = await updateTemplates({
        data: {
          options: { id: ids },
          data: {
            status
          }
        }
      });

      if (!result) {
        toast.error(`Could not ${action_to_take} automation at this time.`);
        return;
      }

      if (status !== status_tab) {
        // The journeys now belong on the other tab, so drop them from this one.
        const moved_ids = items_to_act_on.map((template) => template.id);
        dispatch(
          loadAutomationsToStore(automations.filter((item) => !moved_ids.includes(item.id)))
        );
        setTotalAutomations((count) => Math.max(count - moved_ids.length, 0));
      } else {
        const updated_automations = items_to_act_on.map((template) => ({
          ...template,
          status
        }));
        dispatch(addManyAutomationsToStore(updated_automations));
      }
      toast.success(`Automation ${action_to_take}d successfully`);
    }

    setActionToTake('');
    setItemsToActOn([]);
    setShowConfirmationDialog(() => false);
  };

  const handleItemClick = (payload) => {
    const { id } = payload;
    history(`/editor/automations/${id}`);
  };

  const handleSearchRequest = async (
    keys,
    keyword,
    page,
    population = 10,
    options = { filter_query: '', sort_query: '' }
  ) => {
    if (!keys) return;
    try {
      setLoading(true);
      const query = `status=${status_tab}&${options.filter_query || ''}`;
      const sort_query = options.sort_query || 'sort_by=-created_on';
      const { templates, size } = await searchTemplates(keys, keyword, {
        query_string: `${query}&page=${page}&population=${population}&${sort_query}`
      });

      setTotalAutomations(size);
      dispatch(loadAutomationsToStore(templates));
    } catch (e) {
      dispatch(loadAutomationsToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleStatusTabChange = (value) => {
    dispatch(loadAutomationsToStore([]));
    setStatusTab(value);
  };

  const status_toggle = (
    <SegmentedTabs
      options={status_tabs}
      value={status_tab}
      onChange={handleStatusTabChange}
      label="Journey status"
    />
  );

  const table_actions = (
    <>
      <Button
        icon_name="add"
        text="Create automation"
        onClick={() => history('/editor/automations/new')}
        disabled={!permissions['automation:create'] || !has_subscription}
      />
    </>
  );

  return (
    <PageContentWrapper>
      <AutomationStatBar />
      <Spacer multiple={2} />
      {isMobile ? (
        <>
          {status_toggle}
          <Spacer multiple={2} />
          <MobileDatatable
            key={status_tab}
            config={config}
            action={handleDatatableAction}
            onClick={handleItemClick}
            onListModeChange={setSearchMode}
            onDataRequest={handleDataRequest}
            onSearchRequest={handleSearchRequest}
          />
        </>
      ) : (
        <>
          <WebDatatable
            config={{
              ...config,
              total_count: total_automations
            }}
            has_filter
            filter_config={filter_config(events)}
            header_tabs={status_toggle}
            reset_key={status_tab}
            checkbox
            loading_data={loading}
            table_actions={table_actions}
            action={handleDatatableAction}
            onClick={handleItemClick}
            onDataRequest={handleDataRequest}
            onSearchRequest={handleSearchRequest}
          />
        </>
      )}
      <ConfirmationDialog
        message={`Are you sure you want to ${action_to_take} this template?`}
        callback={handleStatusConfirmation}
        is_open={show_confirmation_dialog}
      />
    </PageContentWrapper>
  );
};
